import moment from 'moment';
import React from 'react';
import { useDispatch } from 'react-redux';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Carrier } from '../../../models/Carrier';
import ServicesContext from '../../../contexts/ServicesContext';
import { strings } from '../../../resources/strings';
import { omnichannelResources } from '../../../resources/omnichannelResources';
import { MainActions } from '../../../actions/MainActions';
import { ManageTransportHelper } from '../../../helpers/ManageTransportHelper';
import { IFilterToListOrdersInTransportOutput } from '../ModalFiltersToListOrdersInTransport/ModalFiltersToListOrdersInTransport';
import { Container, Filter, FilterName, FilterValue, Title, Filters, CopyButton } from './styles';
import { CollectTrackingResultType } from '../../../enums/CollectTrackingResultType';

interface IFiltersProps {
  appliedFilters?: IFilterToListOrdersInTransportOutput;
  marginTop?: string;
}

const AppliedFiltersToListOrdersInTransport: React.FC<IFiltersProps> = ({
  appliedFilters,
  marginTop
}) => {

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const [carrierList, setCarrierList] = React.useState<Carrier[]>([]);
  const [loadingCarrierList, setLoadingCarrierList] = React.useState<boolean>(true);

  React.useEffect(() => {
    getCarrierList();
  }, []);

  const {
    statesService,
    toastService,
    getCarrierListService
  } = React.useContext(ServicesContext);

  const anyAppliedFilter =
    appliedFilters?.numbers ||
    appliedFilters?.shippingStartDate ||
    appliedFilters?.shippingEndDate ||
    (appliedFilters?.stateFilterCode) ||
    appliedFilters?.carrierCode ||
    appliedFilters?.virtualCollect ||
    appliedFilters?.lastConsultStatus ||
    appliedFilters?.periodType ||
    appliedFilters?.fulfilledBy ||
    appliedFilters?.originedBy ||
    (appliedFilters?.locationId && appliedFilters?.locationId > 0);

  const renderFilterNumbers = () => {
    if (!appliedFilters?.numbers) return null;

    let numbers = appliedFilters.numbers;
    if (numbers.length > 20) numbers = numbers.substring(0, 19) + '...';

    return (
      <Filter>
        <FilterName>{strings.Orders}: </FilterName>
        <FilterValue>{numbers}</FilterValue>
        <CopyButton title='Copiar' onClick={() => copyNumbersToClipboard()}><FontAwesomeIcon icon={faCopy} color='gray' size='sm' /></CopyButton>
      </Filter>
    );
  }

  const renderFilterDates = () => {
    if (!appliedFilters?.shippingStartDate && !appliedFilters?.shippingEndDate) return null;

    let value = '';
    value += appliedFilters?.shippingStartDate ? 'de ' + moment(appliedFilters?.shippingStartDate).format('DD/MM/yyyy') : '';
    value += appliedFilters?.shippingEndDate ? ' até ' + moment(appliedFilters?.shippingEndDate).format('DD/MM/yyyy') : '';

    return (
      <Filter>
        <FilterName>{strings.Period}: </FilterName>
        <FilterValue>{value}</FilterValue>
      </Filter>
    );
  }

  const renderFilterOrderState = () => {
    const orderStateFilterValues = ManageTransportHelper.getOrderStateFilterValues();
    const state = orderStateFilterValues.find(x => x.code == appliedFilters?.stateFilterCode) || orderStateFilterValues[0];

    return (
      <Filter>
        <FilterName>{strings.StateOfOrder}: </FilterName>
        <FilterValue>{state?.display || '?'}</FilterValue>
      </Filter>
    );
  }

  const memoFilterCarrier = React.useMemo(() => {
    if (!appliedFilters?.carrierCode) return null;

    const carrierName = loadingCarrierList ? '...' : carrierList?.find(x => x.code === appliedFilters.carrierCode)?.name;

    return (
      <Filter>
        <FilterName>{strings.Carrier}: </FilterName>
        <FilterValue>{carrierName}</FilterValue>
      </Filter>
    );
  }, [appliedFilters, loadingCarrierList, carrierList]);

  const renderFilterVirtualCollect = () => {
    if (!appliedFilters?.virtualCollect) return null;

    let virtualCollectFilterValue = '';
    switch (appliedFilters.virtualCollect) {
      case CollectTrackingResultType[CollectTrackingResultType.Failed]:
        virtualCollectFilterValue = strings.WithFailed;
        break;
      case CollectTrackingResultType[CollectTrackingResultType.PendingSystem]:
        virtualCollectFilterValue = strings.PendingAutomaticRealization;
        break;
      case CollectTrackingResultType[CollectTrackingResultType.PendingUser]:
        virtualCollectFilterValue = strings.PendingManualRealization;
        break;
      case CollectTrackingResultType[CollectTrackingResultType.Success]:
        virtualCollectFilterValue = strings.SuccessfullyDone;
        break;
      default:
        virtualCollectFilterValue = strings.NotDone;
    }

    return (
      <Filter>
        <FilterName>{strings.VirtualCollect}: </FilterName>
        <FilterValue>{virtualCollectFilterValue}</FilterValue>
      </Filter>
    );
  }

  const renderFilterLastConsultStatusType = () => {
    if (!appliedFilters?.lastConsultStatus) return null;

    return (
      <Filter>
        <FilterName>{strings.LastStatus}: </FilterName>
        <FilterValue>{appliedFilters.lastConsultStatus === 'y' ? 'É um problema' : 'Não é um problema'}</FilterValue>
      </Filter>
    );
  }

  const renderFilterPeriodType = () => {
    if (!appliedFilters?.periodType) return null;

    return (
      <Filter>
        <FilterName>{strings.PeriodType}: </FilterName>
        <FilterValue>{ManageTransportHelper.getDescriptionOfPeriodTypeForOrdersInTransport(appliedFilters.periodType)}</FilterValue>
      </Filter>
    );
  }

  const renderFilterFulfilledBy = () => {
    if (!appliedFilters?.fulfilledBy) return null;

    return (
      <Filter>
        <FilterName>{strings.FulfilledBy}: </FilterName>
        <FilterValue>{omnichannelResources.FulfillmentOptions.find(f => f.filterId == appliedFilters?.fulfilledBy)?.title}</FilterValue>
      </Filter>
    );
  }

  const renderFilterOriginedBy = () => {
    if (!appliedFilters?.originedBy) return null;

    return (
      <Filter>
        <FilterName>{strings.OriginatedBy}: </FilterName>
        <FilterValue>{omnichannelResources.OrderOriginOptions.find(f => f.filterId == appliedFilters?.originedBy)?.title}</FilterValue>
      </Filter>
    );
  }

  const renderFilterLocation = () => {
    if (!appliedFilters?.locationId || appliedFilters?.locationId <= 0) return null;

    return (
      <Filter>
        <FilterName>{strings.Location}: </FilterName>
        <FilterValue>{appliedFilters?.locationName}</FilterValue>
      </Filter>
    );
  }

  if (!anyAppliedFilter) {
    return (
      <Container marginTop={marginTop}>
        <Title>{strings.AppliedFilters}</Title>
        <Filters>{strings.None}</Filters>
      </Container>
    );
  }

  return (
    <Container marginTop={marginTop}>
      <Title>{strings.AppliedFilters}</Title>
      <Filters>
        {renderFilterNumbers()}
        {memoFilterCarrier}
        {renderFilterPeriodType()}
        {renderFilterDates()}
        {renderFilterLocation()}
        {renderFilterOrderState()}
        {renderFilterVirtualCollect()}
        {renderFilterLastConsultStatusType()}
        {renderFilterFulfilledBy()}
        {renderFilterOriginedBy()}
      </Filters>
    </Container>
  );

  function getCarrierList() {
    setLoadingCarrierList(true);
    mainActions.setNotWait(true);
    getCarrierListService.handle()
      .then(result => setCarrierList(result?.value?.carriers || []))
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setLoadingCarrierList(false);
      });
  }

  function copyNumbersToClipboard() {
    const text = appliedFilters?.numbers || '';
    try {
      navigator.clipboard.writeText(text);
      toastService.success('Os números dos pedidos foram copiados com sucesso. Agora você pode usar o Ctrl+V para colar em outro lugar!');
    } catch {
      toastService.error('Não foi possível copiar os números dos pedidos. Tente novamente!');
    }
  }
}

export { AppliedFiltersToListOrdersInTransport };